import React, { FC, useContext } from 'react'
import { ThemeContext } from 'styled-components'

import {
    OuterCardWrapper,
    CardContainer,
    ImageSection,
    ImageWrapper,
    NextImageContainer,
    ContentSection,
    Title,
    StatsRow,
    RatingRow,
    RatingText,
    RatingCount,
    Description,
    CTAButton,
    PriceContainer,
    PersonDetails,
    BottomRow,
    PriceWrapper,
    ButtonWrapper,
    FavoriteButton,
    FeaturesWrapper,
    ObjectTopRecommendationPill,
    CheckBox,
    InformationWrapper,
    InputCheckbox,
    CheckBoxLabel,
    BreadcrumbContainer,
    LocationContainer,
    LocationText,
    SeoHref,
    PopularChoiceTagContainer,
    PopularChoiceTagText,
    TraumTagIconStyled,
    TopTraumTagPill,
    TraumTagLabel,
    OfferBadge,
    OfferIcon,
    OfferText,
    FreeCancellationContainer,
    FreeCancellationIcon,
    FreeCancellationText,
    PriceText,
    SlasherPrice,
    OfferWrapper,
    BottomTraumTagContainer,
    BottomTraumTagImage,
    BottomTraumTagText,
    OfferPriceWrapper,
    ContentHeader,
    RegionTitleContainer,
    QuickResponderTag,
    StyledFastResponderIcon,
    BookingHighlights,
    PartnerLogoContainer,
    PartnerLogo,
    WishlistIconContainer,
    TopRecommendationWrapper,
    DTVContainer,
    DTVStarContainer,
    FullStarIcon,
    DTVRatingText,
    DTVImage,
    DTVLabel,
    RatingWrapper,
    DTVSeparator,
    TooltipContainer,
    VirtualTourWrapper,
} from './styles'
import StarsContainer from '../../common/stars-container'
import WishlistSection from '../../common/wishlist-section'
import { ListingSectionObject } from '../../../transports/common/listing-section-transport'
import { useTranslation } from '../../../hooks/locale'
import InlineFeatures from '../features/features'
import NextImage from '../next-image'
import FormattedPrice from '../formatted-price'
import BreadcrumbSection from '../../landing-page/breadcrumb-section'
import { useCardData } from '../../../hooks/use-card-data'
import { stopPropagation } from '../../../utils/dom-utils'
import ImageCarousel from '../../common/image-carousel'
import { getBasePath, getPartnerLogoUrl } from '../../../utils/miscellaneous-utils'
import { BreadcrumbListTransport } from '../../../models/common/breadcrumb-model'
import { Offer } from '../../../models/common/offers-model'
import { SuitablesModel } from '../../../models/common/suitables-model'
import Star from '../../../assets/icons/star.svg'
import get from 'lodash/get'
import { useABVariant } from '../../../utils/hooks'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../common/constants'
import VirtualTourTag from '../../common/virtual-tour-tag'

interface Props {
    index: number
    listingObject: ListingSectionObject
    pageName: string
    sectionName: string
    showCtaButton?: boolean
    showBreadcrumbsSection?: boolean
    showMaxPersonCount?: boolean
    showCheckbox?: boolean
    isSelected?: boolean
    onToggleSelect?: (id: number) => void
    sitePathname?: string
    showRegion?: boolean
    useCarousel?: boolean
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
    isAboveFold?: boolean
    distanceFromParentLocation?: string
    propagateSearchRequestId?: boolean
}

const MAX_STARS = 5
const MAX_VISIBLE_FEATURES = 5

const ObjectOverviewCard: FC<Props> = ({
    index,
    listingObject,
    pageName,
    sectionName,
    showCtaButton = true,
    showBreadcrumbsSection = true,
    showMaxPersonCount = false,
    showCheckbox = false,
    isSelected = false,
    onToggleSelect,
    sitePathname,
    showRegion = true,
    useCarousel = false,
    breadcrumbModel,
    offerModel,
    suitablesModel,
    isAboveFold,
    distanceFromParentLocation,
    propagateSearchRequestId = false,
}) => {
    const { t } = useTranslation()
    const theme = useContext(ThemeContext)

    const cardData = useCardData({
        index,
        listingObject,
        showCheckbox,
        isSelected,
        onToggleSelect,
        isObjectOverview: true,
        showMaxPersonCount,
        showCtaButton,
        sitePathname,
        pageName,
        sectionName,
        propagateSearchRequestId,
        breadcrumbModel,
        offerModel,
        suitablesModel,
    })

    const {
        listingId,
        propertyTitle,
        propertyDescriptionText,
        regionName,
        regionLink,
        cardImages,
        partnerName,
        priceValue,
        priceBeforeOffer,
        priceInBaseUnits,
        priceCurrency,
        priceInfoValue,
        formattedOffer,
        isFreeCancellation,
        shouldShowRating,
        formattedRatingAverage,
        formattedRatingCount,
        hasFeatures,
        computedFeatures,
        traumTagsData,
        currentBadgeTraumTag,
        isTopObject,
        virtualTourVideo,
        showSuperfastResponderTag,
        isDirectBooking,
        truncatedDescription,
        suitablesData,
        handleCardClick,
        handleCheckboxChange,
        handleCtaClick,
        href,
        breadcrumbs,
        classification,
        intersectionTargetRef,
    } = cardData

    const dtvlogoPath = getBasePath('/assets/images/dtv-logo.png')

    const { isProvidedVariantActive: isSlasherPriceActive } = useABVariant(AB_KEY.SLASHER_PRICE, AB_VARIANT_VERSION.ONE)

    return (
        <>
            {showBreadcrumbsSection && breadcrumbs && (
                <BreadcrumbContainer>
                    <BreadcrumbSection
                        breadcrumbs={breadcrumbModel?.format(breadcrumbs, '') || []}
                        url={href}
                        hideMobile={false}
                        containerPadding="0"
                    />
                </BreadcrumbContainer>
            )}
            <OuterCardWrapper onClick={handleCardClick} ref={intersectionTargetRef}>
                <CardContainer>
                    <ImageSection>
                        <ImageWrapper>
                            {showCheckbox && (
                                <InformationWrapper onClick={stopPropagation}>
                                    <CheckBox>
                                        <InputCheckbox
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={handleCheckboxChange}
                                            id={`checkbox-${listingId}`}
                                        />
                                        <CheckBoxLabel htmlFor={`checkbox-${listingId}`} />
                                    </CheckBox>
                                </InformationWrapper>
                            )}
                            {useCarousel ? (
                                <ImageCarousel
                                    images={cardImages}
                                    aspectRatio="66.66%"
                                    imageCarouselBorderRadius="1rem"
                                    imageCarouselMargin="0"
                                    isAboveFold={isAboveFold}
                                    imwidth={partnerName ? undefined : 360}
                                />
                            ) : (
                                cardImages.length > 0 && (
                                    <NextImage
                                        containerComponent={NextImageContainer}
                                        src={cardImages[0].url}
                                        alt={cardImages[0].alt || propertyTitle}
                                        layout="responsive"
                                        width={3}
                                        height={2}
                                        imwidth={partnerName ? undefined : 360}
                                    />
                                )
                            )}
                            {isTopObject && (
                                <ObjectTopRecommendationPill>
                                    <TopRecommendationWrapper>{t('topListing')}</TopRecommendationWrapper>
                                </ObjectTopRecommendationPill>
                            )}
                            {!!virtualTourVideo && (
                                <VirtualTourWrapper>
                                    <VirtualTourTag video={virtualTourVideo} handleCtaClick={handleCtaClick} />
                                </VirtualTourWrapper>
                            )}
                            {traumTagsData.badgeTraumTags.length > 0 &&
                                currentBadgeTraumTag &&
                                currentBadgeTraumTag.Icon && (
                                    <TopTraumTagPill key={currentBadgeTraumTag.id} $showCheckbox={showCheckbox}>
                                        <TraumTagIconStyled as={currentBadgeTraumTag.Icon} />
                                        <TraumTagLabel>{t(currentBadgeTraumTag.label)}</TraumTagLabel>
                                    </TopTraumTagPill>
                                )}
                            {partnerName ? (
                                <PartnerLogoContainer $partnerName={partnerName}>
                                    <PartnerLogo
                                        src={getPartnerLogoUrl(partnerName)}
                                        alt={`${partnerName} logo`}
                                        $partnerName={partnerName}
                                    />
                                </PartnerLogoContainer>
                            ) : (
                                <FavoriteButton onClick={stopPropagation}>
                                    <WishlistIconContainer>
                                        <WishlistSection
                                            containerBorderRadius="1.456rem"
                                            containerHeight="1.5rem"
                                            containerWidth="1.5rem"
                                            containerMargin="0"
                                            containerPadding="0"
                                            width="1.5rem"
                                            height="1.5rem"
                                            listingId={listingId?.toString() || ''}
                                            showBoxShadow={false}
                                            strokeColor="white"
                                            containerBackgroundColor="transparent"
                                        />
                                    </WishlistIconContainer>
                                </FavoriteButton>
                            )}
                        </ImageWrapper>
                        <OfferWrapper>
                            {traumTagsData.bottomTraumTags.map(tag => (
                                <BottomTraumTagContainer key={tag.id}>
                                    {tag.imageUrl && <BottomTraumTagImage src={tag.imageUrl} alt={t(tag.label)} />}
                                    <BottomTraumTagText>{t(tag.label)}</BottomTraumTagText>
                                </BottomTraumTagContainer>
                            ))}
                        </OfferWrapper>
                    </ImageSection>

                    <ContentSection>
                        <ContentHeader>
                            <RegionTitleContainer>
                                {showRegion && (
                                    <LocationContainer>
                                        <SeoHref title={regionName} href={regionLink} onClick={stopPropagation}>
                                            <LocationText>{regionName}</LocationText>
                                        </SeoHref>
                                        {!!distanceFromParentLocation && (
                                            <LocationText> | {distanceFromParentLocation}</LocationText>
                                        )}
                                    </LocationContainer>
                                )}
                                <Title>{propertyTitle}</Title>
                            </RegionTitleContainer>
                            {traumTagsData.popularChoiceTag && (
                                <PopularChoiceTagContainer>
                                    {traumTagsData.popularChoiceTag.Icon && (
                                        <TraumTagIconStyled as={traumTagsData.popularChoiceTag.Icon} />
                                    )}
                                    <PopularChoiceTagText>
                                        {t(traumTagsData.popularChoiceTag.label)}
                                    </PopularChoiceTagText>
                                </PopularChoiceTagContainer>
                            )}
                        </ContentHeader>

                        {propertyDescriptionText && <StatsRow>{propertyDescriptionText}</StatsRow>}

                        <RatingWrapper>
                            {shouldShowRating && (
                                <RatingRow>
                                    <RatingText>{formattedRatingAverage}</RatingText>
                                    <StarsContainer
                                        averageRating={Number(formattedRatingAverage)}
                                        maxStars={MAX_STARS}
                                    />
                                    <RatingCount>{formattedRatingCount}</RatingCount>
                                </RatingRow>
                            )}

                            {classification && (
                                <>
                                    {shouldShowRating && <DTVSeparator />}
                                    <DTVContainer>
                                        <DTVStarContainer>
                                            <FullStarIcon as={Star} />
                                            <DTVRatingText>{get(classification, 'starsCount')} / 5</DTVRatingText>
                                        </DTVStarContainer>
                                        <DTVImage src={dtvlogoPath} alt="DTV Logo" />
                                        <DTVLabel>{t('classification.default.title')}</DTVLabel>
                                        {classification.description && (
                                            <TooltipContainer>{classification.description}</TooltipContainer>
                                        )}
                                    </DTVContainer>
                                </>
                            )}
                        </RatingWrapper>

                        <FeaturesWrapper>
                            {(suitablesData || hasFeatures) && (
                                <InlineFeatures
                                    color={theme.colors.tfwBlack99}
                                    size={1.25}
                                    gap={0.75}
                                    visibleFeatures={MAX_VISIBLE_FEATURES}
                                    features={computedFeatures}
                                    showLabel
                                />
                            )}
                        </FeaturesWrapper>

                        {(showSuperfastResponderTag || isDirectBooking) && (
                            <BookingHighlights>
                                {isDirectBooking && (
                                    <QuickResponderTag>
                                        <StyledFastResponderIcon /> {t('directBookingTagLabel')}
                                    </QuickResponderTag>
                                )}
                                {showSuperfastResponderTag && (
                                    <QuickResponderTag>
                                        <StyledFastResponderIcon /> {t('quickResponderText')}
                                    </QuickResponderTag>
                                )}
                            </BookingHighlights>
                        )}

                        <BottomRow>
                            <OfferPriceWrapper>
                                {formattedOffer && (
                                    <OfferBadge>
                                        <OfferIcon />
                                        <OfferText>
                                            {formattedOffer?.discount || ''} · {formattedOffer?.label || ''}
                                        </OfferText>
                                    </OfferBadge>
                                )}
                                <PriceWrapper>
                                    <PriceContainer>
                                        {priceValue && (
                                            <PriceText>
                                                {isSlasherPriceActive && priceBeforeOffer && (
                                                    <SlasherPrice>{priceBeforeOffer}</SlasherPrice>
                                                )}
                                                <FormattedPrice
                                                    priceInBaseUnits={priceInBaseUnits}
                                                    currency={priceCurrency}
                                                    fallbackPrice={priceValue}
                                                />
                                            </PriceText>
                                        )}
                                        {isFreeCancellation && (
                                            <FreeCancellationContainer>
                                                <FreeCancellationIcon />
                                                <FreeCancellationText>{t('freeCancellation')}</FreeCancellationText>
                                            </FreeCancellationContainer>
                                        )}
                                    </PriceContainer>
                                    {priceInfoValue && <PersonDetails>{priceInfoValue}</PersonDetails>}
                                </PriceWrapper>
                            </OfferPriceWrapper>

                            {showCtaButton && (
                                <ButtonWrapper>
                                    <CTAButton onClick={handleCtaClick}>{t('see_offer')}</CTAButton>
                                </ButtonWrapper>
                            )}
                        </BottomRow>
                    </ContentSection>
                </CardContainer>
            </OuterCardWrapper>
        </>
    )
}

export default ObjectOverviewCard
